(() => {
  if (customElements.get('pupmd-vsl-always-player-v2')) return;

  class PupmdVslAlwaysPlayerV2 extends HTMLElement {
    /** @type {AbortController | null} */
    #abort = null;

    /** @type {number} */
    #scrollY = 0;

    /** @type {boolean} */
    #cinemaOpen = false;

    /** @type {number} */
    #cinemaEnteredAt = 0;

    /** @type {number} */
    #cinemaEnterTimer = 0;

    /** @type {(() => void) | null} */
    #viewportListener = null;

    /** @type {MediaQueryList | null} */
    #mobileQuery = null;

    /** @type {((event: MediaQueryListEvent) => void) | null} */
    #mobileQueryListener = null;

    /** @type {string} */
    #mountedViewport = '';

    /** @returns {boolean} */
    #isRevealed() {
      return document.documentElement.dataset.pupmdVslState === 'revealed';
    }

    /** @returns {'mobile' | 'desktop'} */
    #currentViewport() {
      if (!this.#mobileQuery) {
        this.#mobileQuery = window.matchMedia('(max-width: 768px)');
      }
      return this.#mobileQuery.matches ? 'mobile' : 'desktop';
    }

    /**
     * @param {string} mediaId
     */
    #ensureEmbedScript(mediaId) {
      if (!mediaId) return;

      const src = `https://fast.wistia.com/embed/${mediaId}.js`;
      if (document.querySelector(`script[src="${src}"]`)) return;

      const scriptElement = document.createElement('script');
      scriptElement.src = src;
      scriptElement.async = true;
      scriptElement.type = 'module';
      document.head.appendChild(scriptElement);
    }

    /**
     * @param {'mobile' | 'desktop'} viewport
     */
    #mountViewportPlayer(viewport) {
      if (this.#mountedViewport === viewport) return;

      this.#setPlaying(false, { force: true });

      for (const frameElement of this.querySelectorAll('[data-vsl-viewport]')) {
        if (!(frameElement instanceof HTMLElement)) continue;
        frameElement.replaceChildren();
      }

      const frameElement = this.querySelector(`[data-vsl-viewport="${viewport}"]`);
      const templateElement = this.querySelector(`template[data-vsl-player-template="${viewport}"]`);
      if (!(frameElement instanceof HTMLElement) || !(templateElement instanceof HTMLTemplateElement)) {
        this.#mountedViewport = '';
        return;
      }

      const mediaId = frameElement.dataset.vslMediaId || '';
      this.#ensureEmbedScript(mediaId);

      const playerElement = templateElement.content.firstElementChild?.cloneNode(true);
      if (!(playerElement instanceof HTMLElement)) {
        this.#mountedViewport = '';
        return;
      }

      frameElement.appendChild(playerElement);
      this.#mountedViewport = viewport;
      this.#bindPlayer(playerElement);
    }

    #syncCinemaViewport() {
      const visualViewport = window.visualViewport;
      const viewportHeight = visualViewport?.height ?? window.innerHeight;
      const viewportWidth = visualViewport?.width ?? window.innerWidth;
      const offsetTop = visualViewport?.offsetTop ?? 0;
      const offsetLeft = visualViewport?.offsetLeft ?? 0;

      document.documentElement.style.setProperty('--pupmd-vsl-cinema-vh', `${viewportHeight}px`);
      document.documentElement.style.setProperty('--pupmd-vsl-cinema-vw', `${viewportWidth}px`);
      document.documentElement.style.setProperty('--pupmd-vsl-cinema-top', `${offsetTop}px`);
      document.documentElement.style.setProperty('--pupmd-vsl-cinema-left', `${offsetLeft}px`);
    }

    #bindCinemaViewport() {
      if (this.#viewportListener) return;

      this.#viewportListener = () => {
        this.#syncCinemaViewport();
      };

      this.#syncCinemaViewport();
      window.visualViewport?.addEventListener('resize', this.#viewportListener);
      window.visualViewport?.addEventListener('scroll', this.#viewportListener);
      window.addEventListener('resize', this.#viewportListener);
      window.addEventListener('orientationchange', this.#viewportListener);
    }

    #unbindCinemaViewport() {
      if (!this.#viewportListener) return;

      window.visualViewport?.removeEventListener('resize', this.#viewportListener);
      window.visualViewport?.removeEventListener('scroll', this.#viewportListener);
      window.removeEventListener('resize', this.#viewportListener);
      window.removeEventListener('orientationchange', this.#viewportListener);
      this.#viewportListener = null;

      document.documentElement.style.removeProperty('--pupmd-vsl-cinema-vh');
      document.documentElement.style.removeProperty('--pupmd-vsl-cinema-vw');
      document.documentElement.style.removeProperty('--pupmd-vsl-cinema-top');
      document.documentElement.style.removeProperty('--pupmd-vsl-cinema-left');
    }

    #lockScroll() {
      if (this.#cinemaOpen) return;

      this.#cinemaOpen = true;
      this.#scrollY = window.scrollY;
      this.#cinemaEnteredAt = Date.now();

      const innerElement = this.querySelector('.pupmd-vsl-always-player-v2__inner');
      if (innerElement instanceof HTMLElement) {
        this.style.minBlockSize = `${innerElement.offsetHeight}px`;
      }

      this.#bindCinemaViewport();
      document.documentElement.classList.add('pupmd-vsl-cinema-open');
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    }

    #unlockScroll() {
      if (!this.#cinemaOpen) return;

      this.#cinemaOpen = false;
      this.style.minBlockSize = '';
      this.#unbindCinemaViewport();
      document.documentElement.classList.remove('pupmd-vsl-cinema-open');
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      window.scrollTo(0, this.#scrollY);
    }

    /**
     * @param {boolean} isPlaying
     * @param {{ force?: boolean }} [options]
     */
    #setPlaying(isPlaying, options = {}) {
      const innerElement = this.querySelector('.pupmd-vsl-always-player-v2__inner');
      if (!(innerElement instanceof HTMLElement)) return;

      if (isPlaying) {
        this.#lockScroll();
        innerElement.classList.add('is-playing');
        return;
      }

      const force = options.force === true;
      if (!force && Date.now() - this.#cinemaEnteredAt < 400) return;

      if (this.#cinemaEnterTimer) {
        window.clearTimeout(this.#cinemaEnterTimer);
        this.#cinemaEnterTimer = 0;
      }

      innerElement.classList.remove('is-playing');
      this.#unlockScroll();
    }

    #queueCinemaEnter() {
      if (this.#isRevealed() || this.#cinemaOpen) return;

      if (this.#cinemaEnterTimer) window.clearTimeout(this.#cinemaEnterTimer);

      this.#cinemaEnterTimer = window.setTimeout(() => {
        this.#cinemaEnterTimer = 0;
        if (this.#isRevealed()) return;
        this.#setPlaying(true);
      }, 300);
    }

    #stopCinemaMode() {
      this.#setPlaying(false, { force: true });
      this.#abort?.abort();
      this.#abort = null;
    }

    /**
     * @param {Element} playerElement
     */
    #bindPlayer(playerElement) {
      if (!this.#abort) return;
      const signal = this.#abort.signal;
      const revealSecond = Number(this.dataset.revealSecond);

      playerElement.addEventListener(
        'play',
        () => {
          this.#queueCinemaEnter();
        },
        { signal },
      );

      playerElement.addEventListener(
        'pause',
        () => {
          this.#setPlaying(false);
        },
        { signal },
      );

      playerElement.addEventListener(
        'ended',
        () => {
          this.#setPlaying(false, { force: true });
        },
        { signal },
      );

      playerElement.addEventListener(
        'second-change',
        /**
         * @param {Event} event
         */
        (event) => {
          if (this.#isRevealed()) return;
          if (!(event instanceof CustomEvent)) return;

          const second = typeof event.detail?.second === 'number' ? event.detail.second : NaN;
          if (second < revealSecond) return;

          document.dispatchEvent(new CustomEvent('pupmd:vsl:reveal', { detail: { source: 'wistia' } }));
        },
        { signal },
      );
    }

    connectedCallback() {
      this.#abort = new AbortController();
      const signal = this.#abort.signal;

      document.addEventListener(
        'pupmd:vsl:revealed',
        () => {
          this.#stopCinemaMode();
        },
        { signal },
      );

      this.#mobileQuery = window.matchMedia('(max-width: 768px)');
      this.#mountViewportPlayer(this.#currentViewport());

      this.#mobileQueryListener = () => {
        this.#mountViewportPlayer(this.#currentViewport());
      };

      if (typeof this.#mobileQuery.addEventListener === 'function') {
        this.#mobileQuery.addEventListener('change', this.#mobileQueryListener);
      } else {
        this.#mobileQuery.addListener(this.#mobileQueryListener);
      }
    }

    disconnectedCallback() {
      if (this.#mobileQuery && this.#mobileQueryListener) {
        if (typeof this.#mobileQuery.removeEventListener === 'function') {
          this.#mobileQuery.removeEventListener('change', this.#mobileQueryListener);
        } else {
          this.#mobileQuery.removeListener(this.#mobileQueryListener);
        }
      }

      this.#mobileQueryListener = null;
      this.#stopCinemaMode();
    }
  }

  customElements.define('pupmd-vsl-always-player-v2', PupmdVslAlwaysPlayerV2);
})();
